import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Store, Search, Trash2, Edit, Phone, Mail, MapPin, Upload, Tag, BarChart3, Star, Leaf } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import VendorPortal from "./VendorPortal";
import { DIETARY_OPTIONS } from "../lib/vendorConstants";

export default function VendorsPage() {
  const { authAxios, token, isAdmin, isSubAdmin, isTeamLead, isVendor } = useAuth();
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(searchParams.get("new") === "true");
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [form, setForm] = useState({
    name: "", description: "", categories: [], contact_email: "", contact_phone: "", address: "", menu_items: [], dietary_tags: []
  });
  const [menuItemForm, setMenuItemForm] = useState({ name: "", price: "", category: "" });
  const [uploading, setUploading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewVendor, setReviewVendor] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [vendorReviews, setVendorReviews] = useState([]);
  const [reviewsDialogOpen, setReviewsDialogOpen] = useState(false);

  const canCreateCategory = isAdmin || isSubAdmin || isTeamLead;
  const fetchVendors = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/vendors");
      setVendors(res.data);
    } catch {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchCategories = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/categories");
      setAllCategories(res.data.categories || []);
    } catch {
      // Fallback to empty
    }
  }, [authAxios]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/analytics/vendors");
      setAnalytics(res.data);
    } catch {
      // Silently fail
    }
  }, [authAxios]);

  useEffect(() => {
    fetchVendors();
    fetchCategories();
    fetchAnalytics();
  }, [fetchVendors, fetchCategories, fetchAnalytics]);

  const resetForm = () => {
    setForm({ name: "", description: "", categories: [], contact_email: "", contact_phone: "", address: "", menu_items: [], dietary_tags: [] });
    setMenuItemForm({ name: "", price: "", category: "" });
    setEditingVendor(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const api = authAxios();
      if (editingVendor) {
        await api.put(`/vendors/${editingVendor}`, form);
        toast.success("Vendor updated!");
      } else {
        await api.post("/vendors", form);
        toast.success("Vendor added!");
      }
      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save vendor");
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      const api = authAxios();
      await api.delete(`/vendors/${vendorId}`);
      toast.success("Vendor deleted");
      fetchVendors();
    } catch {
      toast.error("Failed to delete vendor");
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor.vendor_id);
    setForm({
      name: vendor.name, description: vendor.description || "",
      categories: vendor.categories || [], contact_email: vendor.contact_email || "",
      contact_phone: vendor.contact_phone || "", address: vendor.address || "",
      menu_items: vendor.menu_items || [], dietary_tags: vendor.dietary_tags || []
    });
    setDialogOpen(true);
  };

  const addMenuItem = () => {
    if (!menuItemForm.name) return;
    setForm({
      ...form,
      menu_items: [...form.menu_items, { ...menuItemForm, price: parseFloat(menuItemForm.price) || 0 }]
    });
    setMenuItemForm({ name: "", price: "", category: "" });
  };

  const removeMenuItem = (idx) => {
    setForm({ ...form, menu_items: form.menu_items.filter((_, i) => i !== idx) });
  };

  const toggleCategory = (cat) => {
    setForm({
      ...form,
      categories: form.categories.includes(cat)
        ? form.categories.filter((c) => c !== cat)
        : [...form.categories, cat]
    });
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setCreatingCategory(true);
    try {
      const api = authAxios();
      await api.post("/categories", { name });
      toast.success(`Category "${name}" created!`);
      setNewCategoryName("");
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create category");
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleImageUpload = async (vendorId, file) => {
    setUploading(true);
    try {
      const api = authAxios();
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/vendors/${vendorId}`, { image_path: uploadRes.data.path });
      toast.success("Image uploaded!");
      fetchVendors();
    } catch {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewVendor) return;
    try {
      const api = authAxios();
      await api.post(`/vendors/${reviewVendor.vendor_id}/reviews`, { rating: reviewRating, comment: reviewComment });
      toast.success("Review submitted!");
      setReviewDialogOpen(false);
      setReviewComment("");
      setReviewRating(5);
      fetchVendors();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
  };

  const handleViewReviews = async (vendor) => {
    setReviewVendor(vendor);
    try {
      const api = authAxios();
      const res = await api.get(`/vendors/${vendor.vendor_id}/reviews`);
      setVendorReviews(res.data);
      setReviewsDialogOpen(true);
    } catch {
      toast.error("Failed to load reviews");
    }
  };

  const filtered = vendors.filter((v) => {
    const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || v.categories?.includes(categoryFilter);
    return matchesSearch && matchesCategory;
  });

  // Chart colors

  // Vendors with the `vendor` role get their own portal, not this directory.
  if (isVendor) return <VendorPortal />;


  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="vendors-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground">Vendors</h1>
          <p className="text-muted-foreground mt-1">Manage catering vendors and menu offerings</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="shadow-md" data-testid="add-vendor-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} data-testid="vendor-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingVendor ? "Edit Vendor" : "Add New Vendor"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor Name</Label>
                  <Input
                    placeholder="Tasty Catering Co."
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email" placeholder="vendor@example.com"
                    value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    data-testid="vendor-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    data-testid="vendor-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    placeholder="123 Main St, City, State"
                    value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="vendor-address-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe the vendor's specialties..."
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2} data-testid="vendor-description-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Categories</Label>
                  {canCreateCategory && (
                    <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="open-create-category-btn">
                          <Tag className="h-3 w-3 mr-1" /> New Category
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-sm" data-testid="create-category-dialog">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-lg">Create New Category</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 mt-2">
                          <Input
                            placeholder="e.g., Seafood, Organic, Italian"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleCreateCategory()}
                            data-testid="new-category-name-input"
                          />
                          <Button
                            className="w-full" size="sm"
                            onClick={handleCreateCategory}
                            disabled={creatingCategory || !newCategoryName.trim()}
                            data-testid="create-category-submit-btn"
                          >
                            {creatingCategory ? "Creating..." : "Create Category"}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="vendor-categories">
                  {allCategories.map((cat) => (
                    <Badge
                      key={cat}
                      variant={form.categories.includes(cat) ? "default" : "outline"}
                      className="cursor-pointer transition-colors hover:opacity-80"
                      onClick={() => toggleCategory(cat)}
                      data-testid={`category-${cat.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
              {/* Dietary Tags */}
              <div className="space-y-2">
                <Label>Dietary Options</Label>
                <div className="flex flex-wrap gap-2">
                  {DIETARY_OPTIONS.map((tag) => (
                    <Badge
                      key={tag}
                      variant={form.dietary_tags?.includes(tag) ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => {
                        const tags = form.dietary_tags || [];
                        setForm({ ...form, dietary_tags: tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag] });
                      }}
                      data-testid={`dietary-tag-${tag.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <Leaf className="h-2.5 w-2.5 mr-0.5" />{tag}
                    </Badge>
                  ))}
                </div>
              </div>
              {/* Menu Items */}
              <div className="space-y-3">
                <Label>Menu Items & Pricing</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Item name" className="flex-1"
                    value={menuItemForm.name} onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
                    data-testid="menu-item-name-input"
                  />
                  <Input
                    placeholder="Price" className="w-24" type="number" step="0.01"
                    value={menuItemForm.price} onChange={(e) => setMenuItemForm({ ...menuItemForm, price: e.target.value })}
                    data-testid="menu-item-price-input"
                  />
                  <Select value={menuItemForm.category} onValueChange={(v) => setMenuItemForm({ ...menuItemForm, category: v })}>
                    <SelectTrigger className="w-32" data-testid="menu-item-category-select">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCategories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={addMenuItem} data-testid="add-menu-item-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {form.menu_items.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="menu-items-list">
                    {form.menu_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-sm">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-muted-foreground">${item.price?.toFixed(2)}</span>
                          {item.category && <Badge variant="secondary" className="text-xs">{item.category}</Badge>}
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeMenuItem(idx)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full" data-testid="submit-vendor-btn">
                {editingVendor ? "Update Vendor" : "Add Vendor"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Analytics */}
      {analytics && analytics.categories?.length > 0 && (
        <Card className="border-border/50 shadow-sm" data-testid="vendor-analytics-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" /> Category Distribution
                <Badge variant="secondary" className="text-xs ml-2">{analytics.total_vendors} vendors</Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAnalytics(!showAnalytics)} data-testid="toggle-analytics-btn">
                {showAnalytics ? "Hide" : "Show"}
              </Button>
            </div>
          </CardHeader>
          {showAnalytics && (
            <CardContent>
              <div className="space-y-3">
                {analytics.categories.map(({ category, count, percentage }) => {
                  const COLORS = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-blue-500", "bg-purple-500", "bg-orange-500", "bg-teal-500", "bg-red-500", "bg-cyan-500"];
                  const colorIdx = analytics.categories.findIndex((c) => c.category === category) % COLORS.length;
                  return (
                    <div key={category} className="group" data-testid={`analytics-bar-${category.toLowerCase().replace(/\s/g, "-")}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{category}</span>
                        <span className="text-xs text-muted-foreground">{count} vendor{count !== 1 ? "s" : ""} ({percentage}%)</span>
                      </div>
                      <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ease-out ${COLORS[colorIdx]}`}
                          style={{ width: `${Math.max(percentage, 2)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendors..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} className="pl-10"
            data-testid="vendor-search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="vendor-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {allCategories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vendors Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="h-56 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendors-grid">
          {filtered.map((vendor) => (
            <Card
              key={vendor.vendor_id}
              className="border-border/50 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
              data-testid={`vendor-card-${vendor.vendor_id}`}
            >
              {/* Image area */}
              <div className="relative h-36 bg-muted overflow-hidden rounded-t-xl">
                {vendor.image_path ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${vendor.image_path}?auth=${token}`}
                    alt={vendor.name} className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
                    <Store className="h-12 w-12 text-muted-foreground/30" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <label className="cursor-pointer">
                    <input
                      type="file" className="hidden" accept="image/*"
                      onChange={(e) => e.target.files[0] && handleImageUpload(vendor.vendor_id, e.target.files[0])}
                    />
                    <div className="p-1.5 bg-white/90 dark:bg-slate-800/90 rounded-md shadow-sm hover:bg-white transition-colors">
                      <Upload className="h-3.5 w-3.5" />
                    </div>
                  </label>
                  <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7 bg-white/90 dark:bg-slate-800/90 shadow-sm"
                    onClick={() => handleEdit(vendor)} data-testid={`edit-vendor-${vendor.vendor_id}`}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7 bg-white/90 dark:bg-slate-800/90 shadow-sm text-destructive"
                    onClick={() => handleDelete(vendor.vendor_id)} data-testid={`delete-vendor-${vendor.vendor_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg">{vendor.name}</CardTitle>
                  {vendor.avg_rating > 0 && (
                    <button onClick={() => handleViewReviews(vendor)} className="flex items-center gap-1 cursor-pointer hover:opacity-80" data-testid={`vendor-rating-${vendor.vendor_id}`}>
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-medium text-foreground">{vendor.avg_rating}</span>
                      <span className="text-xs text-muted-foreground">({vendor.review_count})</span>
                    </button>
                  )}
                </div>
                {vendor.categories?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {vendor.categories.map((c) => (
                      <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {vendor.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{vendor.description}</p>
                )}
                {vendor.dietary_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {vendor.dietary_tags.map((tag) => (
                      <Badge key={tag} className="text-[10px] bg-emerald-500/10 text-emerald-600 border-0">
                        <Leaf className="h-2.5 w-2.5 mr-0.5" />{tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="space-y-1 text-xs text-muted-foreground">
                  {vendor.contact_email && (
                    <p className="flex items-center gap-1"><Mail className="h-3 w-3" /> {vendor.contact_email}</p>
                  )}
                  {vendor.contact_phone && (
                    <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {vendor.contact_phone}</p>
                  )}
                  {vendor.address && (
                    <p className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {vendor.address}</p>
                  )}
                </div>
                {vendor.menu_items?.length > 0 && (
                  <div className="pt-2 border-t border-border/50">
                    <p className="text-xs font-medium text-foreground mb-1">Menu ({vendor.menu_items.length} items)</p>
                    <div className="flex flex-wrap gap-1">
                      {vendor.menu_items.slice(0, 4).map((item, i) => (
                        <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-md">
                          {item.name} <span className="text-primary">${item.price?.toFixed(2)}</span>
                        </span>
                      ))}
                      {vendor.menu_items.length > 4 && (
                        <span className="text-xs text-muted-foreground">+{vendor.menu_items.length - 4} more</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="pt-2 flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs flex-1" onClick={() => { setReviewVendor(vendor); setReviewRating(5); setReviewComment(""); setReviewDialogOpen(true); }} data-testid={`write-review-${vendor.vendor_id}`}>
                    <Star className="h-3 w-3 mr-1" /> Review
                  </Button>
                  {vendor.review_count > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs" onClick={() => handleViewReviews(vendor)} data-testid={`view-reviews-${vendor.vendor_id}`}>
                      {vendor.review_count} review{vendor.review_count !== 1 ? "s" : ""}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16" data-testid="no-vendors">
          <Store className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
          <h3 className="text-lg font-heading font-semibold text-foreground">No vendors yet</h3>
          <p className="text-muted-foreground mt-1">Add your first catering vendor</p>
          <Button className="mt-4" onClick={() => setDialogOpen(true)} data-testid="add-first-vendor-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Vendor
          </Button>
        </div>
      )}

      {/* Write Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="write-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Review {reviewVendor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex gap-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => setReviewRating(s)} className="p-1 hover:scale-110 transition-transform" data-testid={`star-${s}`}>
                    <Star className={`h-6 w-6 ${s <= reviewRating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Comment (optional)</Label>
              <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Share your experience..." rows={3} data-testid="review-comment-input" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitReview} data-testid="submit-review-btn">Submit Review</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Reviews Dialog */}
      <Dialog open={reviewsDialogOpen} onOpenChange={setReviewsDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="view-reviews-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              Reviews for {reviewVendor?.name}
              {reviewVendor?.avg_rating > 0 && (
                <Badge variant="secondary" className="ml-auto">
                  <Star className="h-3 w-3 mr-0.5 fill-amber-400 text-amber-400" /> {reviewVendor.avg_rating}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {vendorReviews.length > 0 ? vendorReviews.map((r) => (
              <div key={r.review_id} className="p-3 rounded-lg border border-border/50 bg-muted/20" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{r.user_name}</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-3 w-3 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`} />
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mt-1">{r.comment}</p>}
              </div>
            )) : (
              <p className="text-sm text-muted-foreground text-center py-4">No reviews yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
